/**
 * OmnisearchDataGridV2 - All-in-one DataGrid with omnisearch toolbar.
 *
 * Combines DataGridToolbar + SuspenseDataGrid with internal state management:
 * - Omnisearch state from URL (useOmnisearchStore/useOmnisearchActions)
 * - Grid persistence to localStorage (useGridPersistence)
 * - Apollo Suspense queries (useDataGridQuery)
 * - Pagination state management
 *
 * Props are organized by how they should be stabilized:
 * - Module-level constants: arrays/objects without callbacks
 * - Module-level functions: functions without component scope
 * - useMemo: arrays with callbacks (columns, commandButtons)
 * - useCallback: functions with component scope (buildVariables, onRowClick)
 * - Inline: primitives (strings, numbers, booleans)
 *
 * See README.md for the module-level constants pattern documentation.
 */

import { OperationVariables, TypedDocumentNode } from "@apollo/client";
import { Box, SxProps, Theme, useMediaQuery, useTheme } from "@mui/material";
import {
  GridApiPremium,
  GridColumnVisibilityModel,
  GridInitialState,
  GridPaginationModel,
  GridRowHeightParams,
  GridRowHeightReturnValue,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
  GridSlotsComponent,
  GridSortModel,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import { DataGridProps } from "components/DataGrid";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { ReactNode, Suspense, useCallback, useMemo } from "react";
import { HiddenDisplayToken } from "utils/omnisearchUtils";

import { DataGridSkeleton } from "./DataGridSkeleton";
import { DataGridToolbar, OmnisearchSource } from "./DataGridToolbar";
import { DefaultEmptyStateOverlay } from "./DefaultEmptyStateOverlay";
import { useOmnisearchStore } from "./omnisearchStore";
import { SuspenseDataGrid } from "./SuspenseDataGrid";
import { useDataGridQuery } from "./useDataGridQuery";
import { useGridPersistence } from "./useGridPersistence";
import { useGridSelectionApiRef } from "./useGridSelection";
import { usePageForSearch } from "./usePageForSearch";

// =============================================================================
// Types
// =============================================================================

export type OmnisearchCommandButtonsContext = {
  /** Merged search string from buildVariables (same value sent to the API). */
  search: string;
};

type OmnisearchCommandButtons =
  | ReactNode[]
  | ((context: OmnisearchCommandButtonsContext) => ReactNode[]);

export interface OmnisearchDataGridV2Props<
  TData,
  TVariables extends OperationVariables,
  TRow extends GridValidRowModel = GridValidRowModel,
> {
  // ─────────────────────────────────────────────────────────────────────────
  // MODULE-LEVEL CONSTANTS (arrays/objects without callbacks)
  // Define at module level for zero-cost stable references
  // ─────────────────────────────────────────────────────────────────────────

  /** Page size options. Default: [25, 50, 100] */
  pageSizeOptions?: number[];
  /** Initial sort model. Default: [] */
  initialSortModel?: GridSortModel;
  /** Initial page size. Default: 50 */
  initialPageSize?: number;
  /** Initial column visibility (desktop). Will merge with persisted settings. */
  initialColumnVisibilityModel?: GridColumnVisibilityModel;
  /** Initial column visibility for mobile. */
  mobileColumnVisibilityModel?: GridColumnVisibilityModel;
  /** Filter groups for the filter popover */
  filterGroups?: FilterGroup[];
  /** Key aliases for filter mapping (e.g., { tags: "facilityTags" }) */
  keyAliases?: Record<string, string>;
  /** Additional filter keys not in columns */
  additionalFilterKeys?: FilterKey[];
  /**
   * Omnisearch keys that are valid in saved filters but are not column filter
   * keys — e.g. page-level toggles like the fulfillment "View by" selector.
   * Without this they'd be stripped by filterValidKeys when a saved filter is
   * restored. Pass a stable (module-level) array reference to avoid re-renders.
   */
  preservedFilterKeys?: string[];
  /** Additional columns to exclude from the Omnisearch filter popover. ("actions" is excluded by default) */
  excludeFilterColumns?: string[];
  /** Show external filter button. Default: true */
  showFilters?: boolean;
  /**
   * Drop the outer padding and tighten the toolbar-to-grid gap. Default: false.
   *
   * The standalone spacing assumes the grid IS the page. Embedded in a section
   * card that brings its own padding, it doubles up on the sides and leaves a
   * hole between the card's heading and the search row. Opt-in, so no existing
   * grid moves.
   */
  compact?: boolean;
  /** Show manage columns button. Auto-calculates if undefined (>= 5 non-action columns) */
  shouldShowManageColumns?: boolean;
  /** Initial grid state for MUI DataGrid */
  initialState?: GridInitialState;
  /**
   * Custom slots for the DataGrid (e.g., noRowsOverlay).
   * Define at module level for stable references - slot components should
   * use hooks internally rather than receiving props.
   */
  slots?: Partial<GridSlotsComponent>;

  // ─────────────────────────────────────────────────────────────────────────
  // MODULE-LEVEL FUNCTIONS (no component scope dependencies)
  // Define at module level - these don't capture any closure variables
  // ─────────────────────────────────────────────────────────────────────────

  /** GraphQL query document */
  query: TypedDocumentNode<TData, TVariables>;
  /** Extract items array from query data */
  getItems: (data: TData) => TRow[];
  /** Extract total count from query data */
  getCount: (data: TData) => number;
  /** Get unique row ID. Default: row => row.id */
  getRowId?: (row: TRow) => GridRowId;

  // ─────────────────────────────────────────────────────────────────────────
  // useMemo (arrays/objects with callbacks or React elements)
  // Must be memoized because they contain callbacks that capture scope
  // ─────────────────────────────────────────────────────────────────────────

  /** Grid columns (use useMemo - contains renderCell, valueGetter, etc.) */
  columns: OmnisearchGridColDef<TRow>[];
  /**
   * Command buttons for the right side of the toolbar.
   * Pass a static array (useMemo) or a function receiving merged search context (useCallback).
   */
  commandButtons?: OmnisearchCommandButtons;
  /**
   * Command buttons for the left side of the toolbar (before omnisearch).
   * Pass a static array (useMemo) or a function receiving merged search context (useCallback).
   */
  startCommandButtons?: OmnisearchCommandButtons;

  // ─────────────────────────────────────────────────────────────────────────
  // useCallback (functions that use component scope)
  // Must be memoized because they capture component variables in closures
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Build query variables from omnisearch state.
   * Receives: omnisearch string, pagination model, sort model
   * Must return the complete variables object for your query.
   */
  buildVariables: (
    omnisearch: string,
    pagination: { page: number; pageSize: number },
    sort: GridSortModel
  ) => TVariables;
  /** Row click handler (use useCallback if it uses component scope) */
  onRowClick?: (params: GridRowParams<TRow>) => void;
  /** Get row link for clickable row/cell navigation */
  getRowLink?: (row: TRow) => string | null;
  /** CSS class name(s) applied per row (e.g. unread-row styling). */
  getRowClassName?: DataGridProps<TRow>["getRowClassName"];
  /** Get row height dynamically */
  getRowHeight?: (params: GridRowHeightParams) => GridRowHeightReturnValue;
  /** Check if row is selectable */
  isRowSelectable?: (params: GridRowParams<TRow>) => boolean;
  /** Detail panel content getter */
  getDetailPanelContent?: (params: GridRowParams<TRow>) => ReactNode;
  /** Detail panel height getter */
  getDetailPanelHeight?: (params: GridRowParams<TRow>) => number | "auto";

  // ─────────────────────────────────────────────────────────────────────────
  // PRIMITIVES (always stable - can be inline)
  // Strings, numbers, booleans are compared by value, always stable
  // ─────────────────────────────────────────────────────────────────────────

  /** Persistence key for localStorage (grid settings) */
  persistenceKey: string;
  /** Tenant ID for backend queries */
  tenantId?: string;
  /** Message to display when the dataset is empty and no omnisearch filter is active */
  noRowsMessage?: string;
  /** Button text when the dataset is empty and no omnisearch filter is active */
  noDataButtonText?: string;
  /** Button handler when the dataset is empty and no omnisearch filter is active */
  onNoDataButtonClick?: () => void;
  /** Row height. Default: 38 */
  rowHeight?: number | "auto";
  /** Enable checkbox selection. Default: false */
  checkboxSelection?: boolean;
  /** Disable select all checkbox. Default: false */
  disableSelectAll?: boolean;
  /** Disable row selection on click */
  disableRowSelectionOnClick?: boolean;
  /** Hide the footer */
  hideFooter?: boolean;
  /** Skip the query (for conditional fetching) */
  skip?: boolean;
  /**
   * Where this grid's search state lives. Omit it and the grid reads and writes
   * the URL, which is what every page-level grid wants. Pass a
   * `useLocalOmnisearch()` result for a grid inside a drawer or modal: two grids
   * on one route otherwise share the URL's single omnisearch string, so the
   * page's filter reaches the drawer's query and its search box.
   */
  omnisearchSource?: OmnisearchSource;
  /** Passed through to Omnisearch — see {@link HiddenDisplayToken}. Stable reference. */
  hiddenDisplayTokens?: HiddenDisplayToken[];

  // ─────────────────────────────────────────────────────────────────────────
  // CONTROLLED STATE (optional - for advanced use cases)
  // Use these when you need to control selection externally
  // ─────────────────────────────────────────────────────────────────────────

  /** Controlled row selection model */
  rowSelectionModel?: GridRowSelectionModel;
  /** Row selection change handler */
  onRowSelectionModelChange?: (model: GridRowSelectionModel) => void;
  /** Called when items are loaded - can be used to cache row data for selection */
  onItemsLoaded?: (items: TRow[]) => void;

  // ─────────────────────────────────────────────────────────────────────────
  // STYLING
  // ─────────────────────────────────────────────────────────────────────────

  /** Custom styles for the outer container */
  sx?: SxProps<Theme>;
  /** Custom styles for the DataGrid */
  gridSx?: SxProps<Theme>;
  /** API ref for direct grid control */
  apiRef?: React.RefObject<GridApiPremium | null>;
  /** Custom loading fallback */
  fallback?: ReactNode;
  /** Content rendered between the omnisearch toolbar and the grid, for pages whose search drives more than the grid. */
  betweenToolbarAndGrid?: ReactNode;
}

// =============================================================================
// Default values (module-level for stability)
// =============================================================================

const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_INITIAL_SORT_MODEL: GridSortModel = [];
const DEFAULT_INITIAL_PAGE_SIZE = 50;
const DEFAULT_EXCLUDE_FILTER_COLUMNS: string[] = [];

function getSearchFromVariables(variables: OperationVariables, omnisearch: string): string {
  const search = variables.search;
  if (typeof search === "string") {
    return search;
  }
  return omnisearch;
}

function renderCommandButtons(
  commandButtons: OmnisearchCommandButtons | undefined,
  context: OmnisearchCommandButtonsContext
): ReactNode[] {
  if (!commandButtons) return [];

  if (Array.isArray(commandButtons)) {
    return commandButtons;
  }

  if (typeof commandButtons === "function") {
    return commandButtons(context);
  }

  return [];
}

// =============================================================================
// Component
// =============================================================================

export function OmnisearchDataGridV2<
  TData,
  TVariables extends OperationVariables,
  TRow extends GridValidRowModel = GridValidRowModel,
>({
  // Module-level constants
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  initialSortModel = DEFAULT_INITIAL_SORT_MODEL,
  initialPageSize = DEFAULT_INITIAL_PAGE_SIZE,
  initialColumnVisibilityModel,
  mobileColumnVisibilityModel,
  filterGroups,
  keyAliases,
  additionalFilterKeys,
  preservedFilterKeys,
  excludeFilterColumns = DEFAULT_EXCLUDE_FILTER_COLUMNS,
  showFilters = true,
  compact = false,
  shouldShowManageColumns,
  initialState,
  slots,
  // Module-level functions
  query,
  getItems,
  getCount,
  getRowId,
  // useMemo props
  columns,
  commandButtons,
  startCommandButtons,
  // useCallback props
  buildVariables,
  onRowClick,
  getRowLink,
  getRowClassName,
  getRowHeight,
  isRowSelectable,
  getDetailPanelContent,
  getDetailPanelHeight,
  // Primitives
  persistenceKey,
  tenantId,
  noRowsMessage,
  noDataButtonText,
  onNoDataButtonClick,
  rowHeight = 38,
  checkboxSelection = false,
  disableSelectAll = false,
  disableRowSelectionOnClick,
  hideFooter,
  skip = false,
  omnisearchSource,
  hiddenDisplayTokens,
  // Controlled state
  rowSelectionModel,
  onRowSelectionModelChange,
  onItemsLoaded,
  // Styling
  sx,
  gridSx,
  betweenToolbarAndGrid,
  apiRef: apiRefProp,
  fallback,
}: OmnisearchDataGridV2Props<TData, TVariables, TRow>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Get apiRef from context if not provided as prop
  // This allows GridSelectionProvider to auto-wire to the grid
  const contextApiRef = useGridSelectionApiRef();
  const apiRef = apiRefProp ?? contextApiRef;

  // ─────────────────────────────────────────────────────────────────────────
  // Internal omnisearch state (from URL unless the caller supplies a source)
  //
  // The URL hook is called on every render so the hook order never depends on
  // whether a caller passed a source. With no source `omnisearch` is exactly
  // the URL value it was before the prop existed.
  // ─────────────────────────────────────────────────────────────────────────
  const urlState = useOmnisearchStore();
  const omnisearch = omnisearchSource?.omnisearch ?? urlState.omnisearch;

  // ─────────────────────────────────────────────────────────────────────────
  // Grid persistence (sort, columns, page size to localStorage)
  // ─────────────────────────────────────────────────────────────────────────
  const {
    pageSize,
    sortModel,
    columnVisibilityModel,
    setPageSize,
    setSortModel,
    setColumnVisibilityModel,
  } = useGridPersistence({
    persistenceKey,
    initialSortModel,
    initialPageSize,
    initialColumnVisibilityModel: isMobile
      ? (mobileColumnVisibilityModel ?? initialColumnVisibilityModel)
      : initialColumnVisibilityModel,
    isMobile,
    columns,
  });

  // ─────────────────────────────────────────────────────────────────────────
  // Pagination state (page number is local and search-scoped, pageSize is
  // persisted). See usePageForSearch for why the page resets on a filter change.
  // ─────────────────────────────────────────────────────────────────────────
  const { page, setPage } = usePageForSearch(omnisearch);
  const paginationModel = useMemo(() => ({ page, pageSize }), [page, pageSize]);
  const handlePaginationChange = useCallback(
    (model: GridPaginationModel) => {
      setPage(model.page);
      setPageSize(model.pageSize);
    },
    [setPage, setPageSize]
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Build query variables using the provided builder
  // ─────────────────────────────────────────────────────────────────────────
  const variables = useMemo(
    () => buildVariables(omnisearch, paginationModel, sortModel),
    [buildVariables, omnisearch, paginationModel, sortModel]
  );

  const search = useMemo(
    () => getSearchFromVariables(variables, omnisearch),
    [variables, omnisearch]
  );

  const renderedCommandButtons = useMemo(
    () => renderCommandButtons(commandButtons, { search }),
    [commandButtons, search]
  );

  const renderedStartCommandButtons = useMemo(
    () => renderCommandButtons(startCommandButtons, { search }),
    [startCommandButtons, search]
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Apollo Suspense query
  // ─────────────────────────────────────────────────────────────────────────
  const { queryRef, isPending } = useDataGridQuery({
    query,
    variables,
    skip,
  });

  // ─────────────────────────────────────────────────────────────────────────
  // Column filter keys for omnisearch (combine columns + additional keys)
  // ─────────────────────────────────────────────────────────────────────────
  const columnFilterKeys = useMemo(() => {
    const fromColumns = columns
      .filter(
        (col) =>
          !excludeFilterColumns.includes(col.field) &&
          col.filterable !== false &&
          col.type !== "actions"
      )
      .map((col) => ({
        ...col,
        header: col.headerName ?? col.field,
        key: col.field,
      }));
    return additionalFilterKeys ? [...fromColumns, ...additionalFilterKeys] : fromColumns;
  }, [columns, additionalFilterKeys, excludeFilterColumns]);

  // Default empty overlay (V1 parity): "No results found." + Clear search when filtering.
  // Custom slots.noRowsOverlay / noResultsOverlay override this entirely.
  const gridSlots = useMemo((): Partial<GridSlotsComponent> | undefined => {
    const hasCustomEmptyOverlay = slots?.noRowsOverlay != null || slots?.noResultsOverlay != null;

    if (hasCustomEmptyOverlay) {
      return slots;
    }

    function OmnisearchDefaultEmptyOverlay() {
      return (
        <DefaultEmptyStateOverlay
          noDataMessage={noRowsMessage}
          noDataButtonText={noDataButtonText}
          onNoDataButtonClick={onNoDataButtonClick}
        />
      );
    }

    return {
      ...slots,
      noRowsOverlay: OmnisearchDefaultEmptyOverlay,
      noResultsOverlay: OmnisearchDefaultEmptyOverlay,
    };
  }, [slots, noRowsMessage, noDataButtonText, onNoDataButtonClick]);

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ pt: compact ? 0 : 3, px: compact ? 0 : 3, ...sx }}>
      {/* Toolbar */}
      <DataGridToolbar
        compact={compact}
        omnisearchSource={omnisearchSource}
        hiddenDisplayTokens={hiddenDisplayTokens}
        columnFilterKeys={columnFilterKeys}
        filterGroups={filterGroups}
        keyAliases={keyAliases}
        preservedFilterKeys={preservedFilterKeys}
        tenantId={tenantId}
        persistenceKey={persistenceKey}
        showFilters={showFilters}
        columns={columns}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        shouldShowManageColumns={shouldShowManageColumns}
        commandButtons={renderedCommandButtons}
        startCommandButtons={renderedStartCommandButtons}
      />

      {betweenToolbarAndGrid}

      {/* DataGrid with Suspense */}
      <Suspense fallback={fallback ?? <DataGridSkeleton />}>
        <SuspenseDataGrid
          queryRef={queryRef}
          isPending={isPending}
          getItems={getItems}
          getCount={getCount}
          onItemsLoaded={onItemsLoaded}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationChange}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={onRowSelectionModelChange}
          onRowClick={onRowClick}
          getRowLink={getRowLink}
          getRowClassName={getRowClassName}
          isRowSelectable={isRowSelectable}
          getRowId={getRowId}
          rowHeight={rowHeight}
          getRowHeight={getRowHeight}
          checkboxSelection={checkboxSelection}
          disableSelectAll={disableSelectAll}
          disableRowSelectionOnClick={disableRowSelectionOnClick}
          hideFooter={hideFooter}
          pageSizeOptions={pageSizeOptions}
          sx={gridSx}
          initialState={initialState}
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={getDetailPanelHeight}
          apiRef={apiRef ?? undefined}
          slots={gridSlots}
        />
      </Suspense>
    </Box>
  );
}
